import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*                              Helper functions                              */
/* -------------------------------------------------------------------------- */

function parseSku(sku) {
  const [brand, year, size, entry] = sku.split("-");
  const del = sku.endsWith("-DEL");
  return { brand, year, size, entry, del };
}

function mainGroupFromSku(sku) {
  const { brand, year, del } = parseSku(sku);
  if (del) return "GIA-24-DEL";
  if (brand === "GIA" && year === "22") return "GIA-22";
  if (brand === "SUP" && year === "22") return "SUP-22";
  if (brand === "RIE" && year === "22") return "RIE-22";
  if (brand === "WHI" && year === "23") return "WHI-23";
  if (brand === "MER" && year === "24") return "MER-24";
  if (brand === "GIA" && (year === "23" || year === "24")) return "GIA-23";
  return "Other";
}

function standardisedSkuKey(sku) {
  const { brand, year, size, entry, del } = parseSku(sku);
  if (del) return sku;
  if (brand === "GIA" && (year === "23" || year === "24")) {
    return `GIA-23-${size}-${entry}`;
  }
  return sku;
}

const READY_STATUSES = ["new", "in stock"];

/* -------------------------------------------------------------------------- */
/*                         Build inventory + order maps                       */
/* -------------------------------------------------------------------------- */

function mapOpenOrderCounts(orders = []) {
  const map = new Map();
  orders.forEach((order) => {
    order.items
      ?.filter(
        (i) => i.subscription === true && i.subscription_active === false
      )
      .forEach((i) => {
        const key = standardisedSkuKey(i.sku);
        map.set(key, (map.get(key) || 0) + 1);
      });
  });
  return map;
}

function aggregateInventory(items, openOrderCounts) {
  const groupMap = new Map();
  const statusesSet = new Set();
  const globalStatusCounts = {};

  items.forEach((item) => {
    const sku = item.model.SKU;
    const skid = standardisedSkuKey(sku);
    const group = mainGroupFromSku(sku);
    const status = item.status.operationalStatus;

    statusesSet.add(status);
    globalStatusCounts[status] = (globalStatusCounts[status] || 0) + 1;

    if (!groupMap.has(group)) {
      groupMap.set(group, {
        key: group,
        statusCounts: {},
        total: 0,
        readyCount: 0,
        openOrders: 0,
        skus: new Map(),
      });
    }
    const gRow = groupMap.get(group);
    gRow.statusCounts[status] = (gRow.statusCounts[status] || 0) + 1;
    gRow.total += 1;
    if (READY_STATUSES.includes(status)) gRow.readyCount += 1;

    if (!gRow.skus.has(skid)) {
      gRow.skus.set(skid, {
        key: skid,
        statusCounts: {},
        total: 0,
        readyCount: 0,
        openOrders: openOrderCounts.get(skid) || 0,
      });
    }
    const skuRow = gRow.skus.get(skid);
    skuRow.statusCounts[status] = (skuRow.statusCounts[status] || 0) + 1;
    skuRow.total += 1;
    if (READY_STATUSES.includes(status)) skuRow.readyCount += 1;
  });

  groupMap.forEach((gRow) => {
    gRow.skus.forEach((skuRow) => {
      skuRow.readyToSell = skuRow.readyCount - skuRow.openOrders;
    });
    gRow.openOrders = Array.from(gRow.skus.values()).reduce(
      (sum, r) => sum + r.openOrders,
      0
    );
    gRow.readyToSell = gRow.readyCount - gRow.openOrders;
  });

  return {
    rows: Array.from(groupMap.values()).sort((a, b) =>
      a.key.localeCompare(b.key)
    ),
    statuses: Array.from(statusesSet).sort(),
    globalStatusCounts,
  };
}

/* -------------------------------------------------------------------------- */
/*                            Dashboard component                             */
/* -------------------------------------------------------------------------- */

const InventoryDashboard = () => {
  const [inventory, setInventory] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [invRes, ordRes] = await Promise.all([
          axios.get("/api/inventory"),
          axios.get("/api/circuly-orders/open"),
        ]);
        setInventory(invRes.data);
        setOpenOrders(ordRes.data.orders);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openOrderCounts = useMemo(
    () => mapOpenOrderCounts(openOrders),
    [openOrders]
  );

  const { rows, statuses, globalStatusCounts } = useMemo(
    () => aggregateInventory(inventory, openOrderCounts),
    [inventory, openOrderCounts]
  );

  /* expand all groups the first time rows arrive */
  useEffect(() => {
    if (rows.length && expanded.size === 0) {
      setExpanded(new Set(rows.map((r) => r.key)));
    }
  }, [rows, expanded.size]);

  const globalReady =
    (globalStatusCounts["new"] || 0) + (globalStatusCounts["in stock"] || 0);
  const globalOpenOrders = Array.from(openOrderCounts.values()).reduce(
    (a, b) => a + b,
    0
  );
  const globalReadyToSell = globalReady - globalOpenOrders;
  const globalWithCustomer = globalStatusCounts["with customer"] || 0;
  const globalTotal = Object.values(globalStatusCounts).reduce(
    (a, b) => a + b,
    0
  );

  const toggleExpand = (key) => {
    const next = new Set(expanded);
    next.has(key) ? next.delete(key) : next.add(key);
    setExpanded(next);
  };

  /* grid columns: [label] + [statuses] + spacer + Total + On shelf + Open orders + Ready */
  const grid = {
    gridTemplateColumns: `minmax(120px,0.6fr) repeat(${statuses.length},100px) 100px 120px 120px 120px 120px`,
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50 text-sm text-gray-500">
        Loading inventory…
      </div>
    );
  }
  if (error) {
    return <div className="p-4 text-red-600">Error loading data.</div>;
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiCard
          label="Ready to sell"
          value={globalReadyToSell}
          tooltip="(new + in stock) – open orders"
        />
        <KpiCard label="Total bikes" value={globalTotal} />
        <KpiCard label="With customer" value={globalWithCustomer} />
        <KpiCard label="Open order bikes" value={globalOpenOrders} />
      </div>

      {/* Inventory table */}
      <div className="overflow-auto rounded-lg border shadow-sm">
        <table
          className="min-w-full border-collapse"
          style={{ tableLayout: "fixed" }}
        >
          <thead className="bg-joule text-white" style={grid}>
            <tr className="grid" style={grid}>
              <th className="p-2 text-left">Model / SKU</th>
              {statuses.map((s) => (
                <th key={s} className="p-2 text-center">
                  {s}
                </th>
              ))}
              <th /> {/* spacer */}
              <th className="p-2 text-center">Total</th>
              <th className="p-2 text-center">On&nbsp;shelf</th>
              <th className="p-2 text-center">Open&nbsp;orders</th>
              <th className="p-2 text-center">Ready&nbsp;to&nbsp;sell</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {rows.map((g) => {
              const open = expanded.has(g.key);
              return (
                <React.Fragment key={g.key}>
                  {/* group row */}
                  <tr
                    className="grid cursor-pointer bg-gray-200 hover:bg-gray-400"
                    style={grid}
                    onClick={() => toggleExpand(g.key)}
                  >
                    <td className="flex items-center gap-1 p-2 font-medium">
                      {open ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                      {g.key}
                    </td>
                    {statuses.map((s) => (
                      <td key={s} className="p-2 text-center">
                        {g.statusCounts[s] || 0}
                      </td>
                    ))}
                    <td /> {/* spacer */}
                    <td className="p-2 text-center font-semibold">{g.total}</td>
                    <td className="p-2 text-center font-semibold">
                      {g.readyCount}
                    </td>
                    <td className="p-2 text-center font-semibold">
                      {g.openOrders}
                    </td>
                    <td className="p-2 text-center font-semibold">
                      {g.readyToSell}
                    </td>
                  </tr>

                  {/* sku rows */}
                  {open &&
                    Array.from(g.skus.values())
                      .sort((a, b) => a.key.localeCompare(b.key))
                      .map((r) => (
                        <tr key={r.key} className="grid bg-white" style={grid}>
                          <td className="p-2 pl-8 text-sm text-gray-700">
                            {r.key}
                          </td>
                          {statuses.map((s) => (
                            <td key={s} className="p-2 text-center text-sm">
                              {r.statusCounts[s] || 0}
                            </td>
                          ))}
                          <td />
                          <td className="p-2 text-center text-sm font-medium">
                            {r.total}
                          </td>
                          <td className="p-2 text-center text-sm font-medium">
                            {r.readyCount}
                          </td>
                          <td className="p-2 text-center text-sm font-medium">
                            {r.openOrders}
                          </td>
                          <td className="p-2 text-center text-sm font-medium">
                            {r.readyToSell}
                          </td>
                        </tr>
                      ))}
                </React.Fragment>
              );
            })}
          </tbody>

          {/* grand-total row */}
          <tfoot className="bg-gray-700 text-white font-semibold" style={grid}>
            <tr className="grid" style={grid}>
              <td className="p-2 text-left">Grand&nbsp;total</td>
              {statuses.map((s) => (
                <td key={s} className="p-2 text-center">
                  {globalStatusCounts[s] || 0}
                </td>
              ))}
              <td /> {/* spacer */}
              <td className="p-2 text-center">{globalTotal}</td>
              <td className="p-2 text-center">{globalReady}</td>
              <td className="p-2 text-center">{globalOpenOrders}</td>
              <td className="p-2 text-center">{globalReadyToSell}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/* small KPI card */
const KpiCard = ({ label, value, tooltip }) => (
  <div className="h-full rounded-lg border bg-white shadow p-4 text-center">
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-3xl font-bold tabular-nums">{value}</div>
    {tooltip && <div className="text-[10px] text-gray-400">{tooltip}</div>}
  </div>
);

export default InventoryDashboard;
